import React, { useEffect, useState } from 'react';
import { DateFilter, PreDefinedDateFilters } from '../../../types'; // Adjust the import path as needed
import Picker from '../../widgets/components/SimplePicker';
import DatePicker from '../../widgets/components/DatePicker';
import { globalValue } from '../../apps/AppContext';
import { getFiscalYearConfig } from '../../../Constants';
import { startOfDayInBizTZ, endOfDayInBizTZ } from '../../utils/MiscFunctions';


interface DateFilterPickerProps {
  label?: string;
  className?: string;
  selectedDateFilter?: DateFilter;
  periodType?: 'periodic' | 'YTD' | 'BTD';
  rememberCode?: string;
  showYearOnly?: boolean;
  allowCustomRange?: boolean; // when showYearOnly, also let the user switch to a custom from/to range
  onChange: (dateFilter: DateFilter) => void;
  minWidth?: string;
}

const getYearOptions = (isFiscalYear: boolean = false) => {

  const currentYear = new Date().getFullYear();
  const years = [];

  // Generate options for current year and previous 10 years
  for (let i = 0; i <= 10; i++) {
    const year = currentYear - i;
    const option = {
      value: year.toString(),
      label: isFiscalYear ? `FY ${year}-${(year + 1).toString().slice(-2)}` : year.toString()
    };
    years.push(option);

    if (i < 3) { // Log first few options for debugging
    }
  }

  return years;
};

const createYearDateFilter = (year: string, fiscalYearStart?: string): DateFilter => {

  const dateFilter = new DateFilter();
  const yearNum = parseInt(year);


  if (fiscalYearStart && fiscalYearStart !== "01/01") {
    // Handle fiscal year - year represents the starting year of the fiscal year
    const [day, month] = fiscalYearStart.split('/').map(Number);

    // Fiscal year starts in the selected year; ends in the next calendar year.
    // Anchor via startOfDayInBizTZ/endOfDayInBizTZ (not setHours, which zeroes the
    // *browser-local* clock) so the resulting instant is genuinely biz-tz midnight —
    // matching the invariant DateFilterPicker's own setStartOfDay/setEndOfDay rely on
    // when this filter is later switched to "Custom Range".
    dateFilter.fromDate = startOfDayInBizTZ(new Date(yearNum, month - 1, day));
    dateFilter.toDate = endOfDayInBizTZ(new Date(yearNum + 1, month - 1, day - 1));
    dateFilter.predefinedDateFilter = `FY ${year}-${(yearNum + 1).toString().slice(-2)}`;

  } else {

    // Handle calendar year, anchored to biz-tz midnight (see comment above).
    dateFilter.fromDate = startOfDayInBizTZ(new Date(yearNum, 0, 1));
    dateFilter.toDate = endOfDayInBizTZ(new Date(yearNum, 11, 31));
    dateFilter.predefinedDateFilter = `Year ${year}`;

  }



  return dateFilter;
};

const getCurrentFiscalYear = (fiscalYearStart?: string): string => {

  if (!fiscalYearStart || fiscalYearStart === "01/01") {
    const currentYear = new Date().getFullYear().toString();
    return currentYear;
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const [day, month] = fiscalYearStart.split('/').map(Number);


  // Create fiscal year start date for current calendar year
  const fiscalYearStartDate = new Date(currentYear, month - 1, day);

  // If today is before fiscal year start, we're in the previous fiscal year
  const resultYear = today < fiscalYearStartDate ? (currentYear - 1).toString() : currentYear.toString();

  return resultYear;
};

const extractYearFromDateFilter = (predefinedDateFilter?: string): string => {
  if (!predefinedDateFilter) {
    return new Date().getFullYear().toString();
  }

  // Handle both "FY 2024-25" and "Year 2024" formats
  if (predefinedDateFilter.includes('FY ')) {
    const match = predefinedDateFilter.match(/FY (\d{4})/);
    return match ? match[1] : new Date().getFullYear().toString();
  } else if (predefinedDateFilter.includes('Year ')) {
    const match = predefinedDateFilter.match(/Year (\d{4})/);
    return match ? match[1] : new Date().getFullYear().toString();
  }

  return new Date().getFullYear().toString();
};

const DateFilterPicker: React.FC<DateFilterPickerProps> = ({
  selectedDateFilter,
  className,
  rememberCode,
  onChange,
  label,
  periodType = 'periodic',
  showYearOnly = false,
  allowCustomRange = false,
  minWidth = 'w-200px'
}) => {
  const [showDates, setShowDates] = useState<boolean>(selectedDateFilter?.predefinedDateFilter === 'Custom');
  const [yearOnlyMode, setYearOnlyMode] = useState<'year' | 'custom'>(
    selectedDateFilter?.predefinedDateFilter === 'Custom' ? 'custom' : 'year'
  );
  // Fall back to biz-tz start/end-of-today, not a raw `new Date()` — the latter carries
  // today's live time-of-day, which flowed straight through the (now-identity)
  // setStartOfDay/setEndOfDay below whenever a user hit "Custom" before touching either
  // date field, producing a fromDate/toDate with an arbitrary time instead of a day boundary.
  const [localFromDate, setLocalFromDate] = useState<Date>(
    selectedDateFilter?.fromDate || startOfDayInBizTZ(new Date())
  );
  const [localToDate, setLocalToDate] = useState<Date>(
    selectedDateFilter?.toDate || endOfDayInBizTZ(new Date())
  );
  // Get fiscal year configuration using the helper function
  const fiscalYear = globalValue?.bizMaster?.bizDetails?.fiscalYear;
  const fiscalYearConfig = getFiscalYearConfig(fiscalYear);
  const fiscalYearStart = fiscalYearConfig?.start;
  const isFiscalYear = !!(fiscalYearStart && fiscalYearStart !== "01/01");


  const [selectedYear, setSelectedYear] = useState<string>(() => {


    if (selectedDateFilter?.predefinedDateFilter) {
      const extractedYear = extractYearFromDateFilter(selectedDateFilter.predefinedDateFilter);

      return extractedYear;
    }

    const defaultYear = isFiscalYear ? getCurrentFiscalYear(fiscalYearStart) : new Date().getFullYear().toString();


    return defaultYear;
  });

  // `date` here is always already anchored to biz-timezone midnight — it comes straight from
  // DatePicker's onChange (toStartOfDayInBizTZ) or from state that was itself stored that way.
  // Re-deriving it via `date.toLocaleString(..., { timeZone }) -> new Date(string)` silently
  // reparses the (timezone-less) wall-clock string in the *browser's* timezone instead of the
  // biz timezone, shifting the instant whenever the two differ — that's what caused a selected
  // date to render as the previous day. Just pass it through / offset it arithmetically instead.
  const setStartOfDay = (date: Date): Date => date;

  const setEndOfDay = (date: Date): Date => new Date(date.getTime() + 24 * 60 * 60 * 1000 - 1);

  const [currentRememberCode, setCurrentRememberCode] = useState<string>();

  useEffect(() => {
    if (selectedDateFilter?.predefinedDateFilter === 'Custom') {
      setShowDates(true);
      setYearOnlyMode('custom');
    }
  }, [selectedDateFilter]);

  // Update selected year when fiscal year configuration changes
  useEffect(() => {
    if (showYearOnly && !selectedDateFilter?.predefinedDateFilter) {
      const defaultYear = isFiscalYear ? getCurrentFiscalYear(fiscalYearStart) : new Date().getFullYear().toString();

      setSelectedYear(defaultYear);

      // Set default date filter
      const defaultDateFilter = createYearDateFilter(defaultYear, fiscalYearStart);

      onChange(defaultDateFilter);
    }
  }, [fiscalYearStart, isFiscalYear, showYearOnly]);

  const handleFromDateChange = (date: Date) => {
    const newFromDate = setStartOfDay(date || new Date());
    setLocalFromDate(newFromDate);

    if (selectedDateFilter) {
      selectedDateFilter.fromDate = newFromDate;
      onChange(selectedDateFilter);
    }

    if (rememberCode) {
      localStorage.setItem(rememberCode, JSON.stringify(selectedDateFilter));
    }
  };

  const handleToDateChange = (date: Date) => {
    const newToDate = setEndOfDay(date || new Date());
    setLocalToDate(newToDate);

    if (selectedDateFilter) {
      selectedDateFilter.toDate = newToDate;
      onChange(selectedDateFilter);
    }

    if (rememberCode) {
      localStorage.setItem(rememberCode, JSON.stringify(selectedDateFilter));
    }
  };

  const handleOptionChange = (selectedOption: string) => {
    const isCustom = selectedOption === 'Custom';
    setShowDates(isCustom);

    const dateFilter = new DateFilter();
    if (isCustom) {
      // For YTD/BTD reports the "From date" picker is hidden — don't set fromDate since
      // the backend derives it from toDate (fiscal year start). Setting it to today would
      // produce fromDate > toDate and return no data.
      if (periodType === 'periodic') {
        dateFilter.fromDate = setStartOfDay(localFromDate || startOfDayInBizTZ(new Date()));
      }
      dateFilter.toDate = setEndOfDay(localToDate || endOfDayInBizTZ(new Date()));
      dateFilter.predefinedDateFilter = selectedOption;
    } else {
      dateFilter.predefinedDateFilter = selectedOption;
      dateFilter.updateDates(periodType); // Pass timezone here
    }

    // Update local state immediately
    if (dateFilter.fromDate) setLocalFromDate(dateFilter.fromDate);
    if (dateFilter.toDate) setLocalToDate(dateFilter.toDate);
    if (rememberCode) {
      localStorage.setItem(rememberCode, JSON.stringify(dateFilter));
    }
    onChange(dateFilter);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setYearOnlyMode('year');

    const dateFilter = createYearDateFilter(year, fiscalYearStart);

    // Update local state immediately
    if (dateFilter.fromDate) setLocalFromDate(dateFilter.fromDate);
    if (dateFilter.toDate) setLocalToDate(dateFilter.toDate);

    if (rememberCode) {
      localStorage.setItem(rememberCode, JSON.stringify(dateFilter));
    }

    onChange(dateFilter);
  };

  const handleSwitchToCustom = () => {
    setYearOnlyMode('custom');
    const dateFilter = new DateFilter();
    dateFilter.fromDate = setStartOfDay(localFromDate || startOfDayInBizTZ(new Date()));
    dateFilter.toDate = setEndOfDay(localToDate || endOfDayInBizTZ(new Date()));
    dateFilter.predefinedDateFilter = 'Custom';
    onChange(dateFilter);
  };

  const getYearLabel = () => {
    if (isFiscalYear) {
      return label || 'Select Fiscal Year'.toTranslatedDisplay();
    }
    return label || 'Select Year'.toTranslatedDisplay();
  };

  const getSelectedYearOption = (selectedYear: string, isFiscalYear: boolean = false) => {
    const options = getYearOptions(isFiscalYear);
    return options.find(option => option.value === selectedYear);
  };

  // Add this useEffect after the existing useEffects
  useEffect(() => {
    if (rememberCode && rememberCode !== currentRememberCode) {

      setCurrentRememberCode(rememberCode);

      // Try to load saved filter from localStorage
      try {
        const savedFilter = localStorage.getItem(rememberCode);
        if (savedFilter) {
          const parsedFilter = JSON.parse(savedFilter);
          const dateFilter = new DateFilter(parsedFilter);

          // Only update if it's different from current
          if (dateFilter.predefinedDateFilter !== selectedDateFilter?.predefinedDateFilter) {
            setShowDates(dateFilter.predefinedDateFilter === 'Custom');
            if (dateFilter.fromDate) setLocalFromDate(new Date(dateFilter.fromDate));
            if (dateFilter.toDate) setLocalToDate(new Date(dateFilter.toDate));
            onChange(dateFilter);
          }
        }
      } catch (error) {
        console.warn('Failed to parse saved date filter:', error);
      }
    }
  }, [rememberCode, currentRememberCode]);

  useEffect(() => {
    if (selectedDateFilter?.fromDate && selectedDateFilter?.toDate) {
      setLocalFromDate(new Date(selectedDateFilter.fromDate));
      setLocalToDate(new Date(selectedDateFilter.toDate));
    }
  }, [selectedDateFilter?.fromDate, selectedDateFilter?.toDate]);

  return (
    <div className={`date-filter-picker ${className} ${(selectedDateFilter?.predefinedDateFilter === 'Custom') ? 'custom-date-filterWidth' : minWidth}`}>
      {showYearOnly ? (
        <>
          <Picker
            label={getYearLabel()}
            options={allowCustomRange
              ? [...getYearOptions(isFiscalYear), { value: 'Custom', label: 'Custom Range' }]
              : getYearOptions(isFiscalYear)}
            placeholder={isFiscalYear ? 'Select Fiscal Year'.toTranslatedDisplay() : 'Select Year'.toTranslatedDisplay()}
            value={yearOnlyMode === 'custom' ? 'Custom' : (getSelectedYearOption(selectedYear)?.value || selectedYear)}
            onSelectionChange={(selectedOption) => {

              if (selectedOption?.value === 'Custom') {
                handleSwitchToCustom();
              } else if (selectedOption && selectedOption.value) {
                handleYearChange(selectedOption.value);
              } else {
                console.warn('⚠️ selectedOption or selectedOption.value is missing');
              }
            }}
            name="yearFilter"
          />
          {yearOnlyMode === 'custom' && (
            <div className="row mt-3">
              <div className="col">
                <label className="form-label">{'From date'.toTranslatedDisplay()}</label>
                <DatePicker
                  value={localFromDate}
                  onChange={handleFromDateChange}
                  className="form-control"
                  placeholder={'From Date'.toTranslatedDisplay()}
                />
              </div>
              <div className="col">
                <label className="form-label">{'To date'.toTranslatedDisplay()}</label>
                <DatePicker
                  value={localToDate}
                  onChange={handleToDateChange}
                  className="form-control"
                  placeholder={'To Date'.toTranslatedDisplay()}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <Picker
            label={label}
            options={PreDefinedDateFilters}
            placeholder={'Select Date'.toTranslatedDisplay()}
            value={selectedDateFilter?.predefinedDateFilter}
            onSelectionChange={(selectedOption) => handleOptionChange(selectedOption.value)}
            name="dateFilter"
          />
          {showDates && (
            <div className="row mt-3">
              {periodType === 'periodic' && (
                <div className="col">
                  <label className="form-label">{'From date'.toTranslatedDisplay()}</label>
                  <DatePicker
                    value={localFromDate}
                    onChange={handleFromDateChange}
                    className="form-control"
                    placeholder={'From Date'.toTranslatedDisplay()}
                  />
                </div>
              )}
              <div className="col">
                <label className="form-label">{'To date'.toTranslatedDisplay()}</label>
                <DatePicker
                  value={localToDate}
                  onChange={handleToDateChange}
                  className="form-control"
                  placeholder={'To Date'.toTranslatedDisplay()}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DateFilterPicker;